import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import api from '../../api';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import SuccessModal from '../../components/common/SuccessModal';
import FailModal from '../../components/common/FailModal';
import { usePagination } from '../../hooks/usePagination';
import { useSelection } from '../../hooks/useSelection';
import { validateField } from '../../utils/validation';
import { useAuth } from '../../contexts/AuthProvider';
import { GenericView, SearchInput } from '../../components/common/table';

const CommercialTerms = () => {
  // Actions dropdown refs for manual Bootstrap Dropdown initialization
  const actionsToggleRef = useRef(null);
  const actionsContainerRef = useRef(null);
  const actionsDropdownInstanceRef = useRef(null);
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  useEffect(() => {
    const toggleEl = actionsToggleRef.current;
    const containerEl = actionsContainerRef.current;
    if (!toggleEl || !containerEl) return;
    if (typeof window !== 'undefined' && window.bootstrap?.Dropdown) {
      const instance = new window.bootstrap.Dropdown(toggleEl, { autoClose: true });
      actionsDropdownInstanceRef.current = instance;
      const onShow = () => setIsActionsOpen(true);
      const onHide = () => setIsActionsOpen(false);
      containerEl.addEventListener('show.bs.dropdown', onShow);
      containerEl.addEventListener('hide.bs.dropdown', onHide);
      return () => {
        containerEl.removeEventListener('show.bs.dropdown', onShow);
        containerEl.removeEventListener('hide.bs.dropdown', onHide);
        try { instance.dispose(); } catch {}
        actionsDropdownInstanceRef.current = null;
      };
    }
  }, []);

  // Permission system
  const { user, can, capabilitiesLoading } = useAuth();
  const canCommercialTerms = (key) => can(key, 'commercialTerms');

    const navigate = useNavigate();
    const pagination = usePagination(10);
    const [search, setSearch] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');

    const handleSearchInputChange = (value) => {
        setSearch(value);
        setSearchTerm(value);
    };

    const handleSearchSubmit = () => {
        setAppliedSearchTerm(searchTerm);
        pagination.setPage(0);
    };

    const {
        selectedIds,
        setSelectedIds,
        handleCheckboxClick,
        handleSelectAll
    } = useSelection();

    const [items, setItems] = useState([]);
    const [services, setServices] = useState([]);
    const [proposalTemplateOptions, setProposalTemplateOptions] = useState([]);
    const [formData, setFormData] = useState({
        code: '',
        name: '',
        description: '',
        serviceId: '',
        proposalTemplateId: ''
    });
    const [editingId, setEditingId] = useState(null);
    const [errors, setErrors] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [confirmAction, setConfirmAction] = useState(null);
    const [showFailModal, setShowFailModal] = useState(false);
    const [failMessage, setFailMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [duplicatingId, setDuplicatingId] = useState(null);

    const fetchList = async (searchValue = appliedSearchTerm) => {
        try {
            setIsLoading(true);
            const response = await api.get('/md/commercial-terms/templates', {
                params: {
                    page: pagination.page,
                    size: pagination.size,
                    search: searchValue || undefined
                }
            });
            const content = response.data.content ?? response.data;
            const list = Array.isArray(content) ? content : (content.content || []);
            setItems(list);
            const totalPages = response.data.totalPages ?? Math.ceil((response.data.totalElements || list.length) / pagination.size);
            pagination.setTotalPages(totalPages);
            pagination.setCurrentPageItems(list.length);
            pagination.setTotal(response.data.totalElements ?? list.length);
        } catch (error) {
            console.error('Error fetching commercial terms templates:', error);
            setFailMessage(error.response?.data?.message || 'Failed to fetch commercial terms templates.');
            setShowFailModal(true);
            setItems([]);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchServices = async () => {
        try {
            const res = await api.get('/md/company-service');
            setServices(Array.isArray(res.data) ? res.data : []);
        } catch (_) {
            setServices([]);
        }
    };

    const fetchProposalTemplates = async () => {
        try {
            const res = await api.get('/crm/proposal-templates');
            setProposalTemplateOptions(Array.isArray(res.data) ? res.data : []);
        } catch (_) {
            setProposalTemplateOptions([]);
        }
    };

    useEffect(() => {
        fetchList(appliedSearchTerm);
    }, [pagination.page, pagination.size, appliedSearchTerm]);

    useEffect(() => {
        fetchServices();
    }, []);

    useEffect(() => {
        fetchProposalTemplates();
    }, []);

    const handleCreate = () => {
    if (!canCommercialTerms('create')) { setFailMessage('You do not have permission to create.'); setShowFailModal(true); return; }
        setFormData({ code: '', name: '', description: '', serviceId: '', proposalTemplateId: '' });
        setEditingId(null);
        setErrors({});
        setShowModal(true);
        const modal = document.getElementById('modal-commercial-terms');
        if (window.bootstrap?.Modal && modal) new window.bootstrap.Modal(modal).show();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const codeError = validateField('name', formData.code, { required: true });
        if (codeError) {
            setErrors({ code: 'Code is required' });
            return;
        }
        setErrors({});
        const payload = {
            code: formData.code.trim(),
            name: (formData.name || formData.code).trim(),
            description: (formData.description || '').trim() || null,
            serviceId: formData.serviceId ? Number(formData.serviceId) : null,
            proposalTemplateId: formData.proposalTemplateId ? Number(formData.proposalTemplateId) : null,
            terms: []
        };
        try {
            const res = await api.post('/md/commercial-terms/templates', payload);
            const created = res.data;
            setSuccessMessage('Commercial terms template created successfully. You can now edit its sections and points.');
            setShowSuccessModal(true);
            setShowModal(false);
            setEditingId(null);
            fetchList();
            const modal = document.getElementById('modal-commercial-terms');
            if (window.bootstrap?.Modal?.getInstance(modal)) window.bootstrap.Modal.getInstance(modal).hide();
            if (created && created.id) {
                navigate(`/masterdata-commercial-terms/${created.id}`, { state: { template: created } });
            }
        } catch (error) {
            const msg = error.response?.data || error.message || 'Failed to save.';
            setFailMessage(typeof msg === 'string' ? msg : JSON.stringify(msg));
            setShowFailModal(true);
        }
    };

    const deleteSelected = () => {
    if (!canCommercialTerms('delete')) { setFailMessage('You do not have permission to delete.'); setShowFailModal(true); return; }
        if (selectedIds.length === 0) {
            setFailMessage('Please select at least one item to delete.');
            setShowFailModal(true);
            return;
        }
        setConfirmAction(() => async () => {
            try {
                for (const id of selectedIds) {
                    await api.delete(`/md/commercial-terms/templates/${id}`);
                }
                setSelectedIds([]);
                fetchList();
                setShowDeleteConfirmation(false);
                setSuccessMessage('Selected template(s) deleted successfully.');
                setShowSuccessModal(true);
            } catch (error) {
                setShowDeleteConfirmation(false);
                setFailMessage(error.response?.data?.message || 'Failed to delete.');
                setShowFailModal(true);
            }
        });
        setShowDeleteConfirmation(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (name === 'code') setErrors((prev) => ({ ...prev, code: null }));
    };

    const handleDuplicate = async (row) => {
        if (duplicatingId != null) return;
        setDuplicatingId(row.id);
        try {
            const res = await api.get(`/md/commercial-terms/templates/${row.id}`);
            const data = res.data || {};
            const terms = Array.isArray(data.terms) ? data.terms : [];
            const baseCode = (row.code || data.id || 'template').replace(/-copy(-\d+)?$/, '');
            const newCode = `${baseCode}-copy-${Date.now()}`;
            const payload = {
                code: newCode,
                name: ((row.name || data.name || row.code) + ' (Copy)').trim(),
                description: data.description || null,
                serviceId: data.serviceId ?? row.serviceId ?? null,
                proposalTemplateId: data.proposalTemplateId ?? row.proposalTemplateId ?? null,
                terms
            };
            const createRes = await api.post('/md/commercial-terms/templates', payload);
            const created = createRes.data;
            setSuccessMessage('Commercial terms template duplicated successfully. You can now edit the copy.');
            setShowSuccessModal(true);
            fetchList();
            if (created?.id) {
                navigate(`/masterdata-commercial-terms/${created.id}`, { state: { template: created } });
            }
        } catch (error) {
            const msg = error.response?.data || error.message || 'Failed to duplicate.';
            setFailMessage(typeof msg === 'string' ? msg : JSON.stringify(msg));
            setShowFailModal(true);
        } finally {
            setDuplicatingId(null);
        }
    };

    const formatDate = (val) => (val ? new Date(val).toLocaleString() : '');

    const columns = [
        { key: 'code', label: 'Code', sortable: true },
        { key: 'name', label: 'Name', sortable: true },
        { key: 'description', label: 'Description', sortable: false, render: (value) => value ? String(value).slice(0, 60) + (value.length > 60 ? '…' : '') : '-' },
        { key: 'serviceName', label: 'Service', sortable: true, render: (value, row) => value || (row.serviceId ? `ID ${row.serviceId}` : '-') },
        { key: 'isActive', label: 'Active', sortable: true, render: (value) => value ? 'Yes' : 'No' },
        { key: 'createdAt', label: 'Created', sortable: true, render: (value) => formatDate(value) },
        { key: 'updatedAt', label: 'Updated', sortable: true, render: (value) => formatDate(value) },
        { key: 'cuserName', label: 'Created By', sortable: true, render: (value) => value || '-' },
        { key: 'uuserName', label: 'Updated By', sortable: true, render: (value) => value || '-' },
        {
            key: '_actions',
            label: 'Action',
            sortable: false,
            render: (value, row) => (
                <div className="btn-list flex-nowrap">
                    <a
                        href="#"
                        title="Edit"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate(`/masterdata-commercial-terms/${row.id}`, { state: { template: row } });
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-edit"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" /><path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z" /><path d="M16 5l3 3" /></svg>
                    </a>
                    <a
                        href="#"
                        title="Duplicate"
                        className={duplicatingId === row.id ? 'disabled' : ''}
                        style={{ pointerEvents: duplicatingId === row.id ? 'none' : 'auto' }}
                        onClick={(e) => {
                            e.preventDefault();
                            if (duplicatingId === row.id) return;
                            handleDuplicate(row);
                        }}
                    >
                        {duplicatingId === row.id ? (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-copy"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M7 7m0 2.667a2.667 2.667 0 0 1 2.667 -2.667h8.666a2.667 2.667 0 0 1 2.667 2.667v8.666a2.667 2.667 0 0 1 -2.667 2.667h-8.666a2.667 2.667 0 0 1 -2.667 -2.667z" /><path d="M4.012 16.737a2.005 2.005 0 0 1 -1.012 -1.737v-10c0 -1.1 .9 -2 2 -2h10c.75 0 1.158 .385 1.5 1" /></svg>
                        )}
                    </a>
                </div>
            )
        },
    ];

    return (
        <>
            <div className="page-header d-print-none">
                <div className="container-xl">
                    <div className="row g-2 align-items-center">
                        <div className="col">
                            <h2 className="page-title">Commercial Terms Templates</h2>
                        </div>
                        <div className="col-auto ms-auto d-print-none">
                            <div className="btn-list">
                                <a href="#" className="btn btn-primary d-none d-sm-inline-block" onClick={handleCreate}>
                                    <Plus className="icon me-1" size={18} />
                                    Create
                                </a>
                                <a href="#" className="btn btn-primary d-sm-none btn-icon" onClick={handleCreate} aria-label="Create">
                                    <Plus size={18} />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body">
                <div className="container-xl">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-body">
                                <GenericView
                                    data={items}
                                    columns={columns}
                                    isLoading={isLoading || capabilitiesLoading}
                                    emptyStateMessage="No commercial terms found"
                                    checkboxSelection={true}
                                    selectedRows={selectedIds}
                                    onSelectRow={(itemId, isSelected) => {
                                        if (isSelected) {
                                            setSelectedIds([...selectedIds, itemId]);
                                        } else {
                                            setSelectedIds(selectedIds.filter(id => id !== itemId));
                                        }
                                    }}
                                    onSelectAllRows={(itemIds) => {
                                        if (Array.isArray(itemIds) && itemIds.length > 0) {
                                            setSelectedIds([...new Set([...selectedIds, ...itemIds])]);
                                        } else {
                                            setSelectedIds([]);
                                        }
                                    }}
                                    onDelete={canCommercialTerms('delete') ? (item) => {
                                        setSelectedIds([item.id]);
                                        deleteSelected();
                                    } : undefined}
                                    searchComponent={
                                        <div className="d-flex gap-2">
                                            <SearchInput
                                                value={searchTerm}
                                                onChange={handleSearchInputChange}
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        handleSearchSubmit();
                                                    }
                                                }}
                                                placeholder="Search..."
                                                uniqueId="commercial-terms-search"
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                onClick={handleSearchSubmit}
                                            >
                                                Search
                                            </button>
                                        </div>
                                    }
                                    actionButtons={
                                        <div className="d-flex gap-2">
                                            {canCommercialTerms('delete') && (
                                                <div ref={actionsContainerRef} className="dropdown">
                                                    <a
                                                        ref={actionsToggleRef}
                                                        href="#"
                                                        className="btn dropdown-toggle"
                                                        data-bs-toggle="dropdown"
                                                        aria-expanded={isActionsOpen}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            const inst = actionsDropdownInstanceRef.current;
                                                            if (inst?.toggle) inst.toggle();
                                                            else setIsActionsOpen(prev => !prev);
                                                        }}
                                                    >Actions</a>
                                                    <div className="dropdown-menu">
                                                        <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); deleteSelected(); }}
                                                            style={{ color: selectedIds.length ? "" : "#ccc", pointerEvents: selectedIds.length ? "auto" : "none" }}>
                                                            Delete
                                                        </a>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    }
                                    pagination={{
                                        currentPage: pagination.page + 1,
                                        totalRows: pagination.total,
                                        rowsPerPage: pagination.size,
                                        onPageChange: (page) => pagination.setPage(page - 1),
                                        onRowsPerPageChange: (size) => {
                                            pagination.setSize(size);
                                            pagination.setPage(0);
                                        }
                                    }}
                                    pageSizeOptions={[10, 25, 50, 100]}
                                    uniqueId="commercial-terms-table"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="modal modal-blur fade" id="modal-commercial-terms" tabIndex="-1">
                <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{editingId ? 'Edit Commercial Terms Template' : 'Create Commercial Terms Template'}</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Code <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.code ? 'is-invalid' : ''}`}
                                        name="code"
                                        value={formData.code}
                                        onChange={handleInputChange}
                                        placeholder="e.g. certification, training"
                                        disabled={!!editingId}
                                    />
                                    {errors.code && <div className="invalid-feedback">{errors.code}</div>}
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Name</label>
                                    <input type="text" className="form-control" name="name" value={formData.name} onChange={handleInputChange} placeholder="Display name" />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Description</label>
                                    <textarea className="form-control" name="description" value={formData.description} onChange={handleInputChange} rows={2} placeholder="Optional description" />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Service</label>
                                    <select className="form-select" name="serviceId" value={formData.serviceId} onChange={handleInputChange}>
                                        <option value="">— None —</option>
                                        {services.map((s) => (
                                            <option key={s.id} value={s.id}>{s.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Proposal Template</label>
                                    <select className="form-select" name="proposalTemplateId" value={formData.proposalTemplateId} onChange={handleInputChange}>
                                        <option value="">— None —</option>
                                        {proposalTemplateOptions.map((t) => (
                                            <option key={t.id} value={t.id}>{t.templateName ?? t.name ?? t.id}</option>
                                        ))}
                                    </select>
                                    <small className="text-muted">Link this commercial terms template to a proposal template so it appears when that template is selected.</small>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <a href="#" className="btn btn-link link-secondary" data-bs-dismiss="modal">Cancel</a>
                                <button type="submit" className="btn btn-primary">{editingId ? 'Update' : 'Save'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <ConfirmationModal
                isOpen={showDeleteConfirmation}
                onClose={() => setShowDeleteConfirmation(false)}
                onConfirm={confirmAction || (() => {})}
                title="Delete Confirmation"
                message="Are you sure you want to delete the selected template(s)?"
                confirmText="Delete"
                cancelText="Cancel"
                type="danger"
            />
            <SuccessModal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)} title="Success" message={successMessage} buttonText="OK" />
            <FailModal isOpen={showFailModal} onClose={() => setShowFailModal(false)} title="Error" message={failMessage} buttonText="OK" />
        </>
    );
};

export default CommercialTerms;
